import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import { Role } from '@tp-vision/roles-permissions';
import { t } from 'i18next';
import { Control, Controller, FieldError, FieldValues, Path } from 'react-hook-form';
import Select from 'react-select';
import { z } from 'zod';
import { components } from '~components/ui/Select';
import { getRoleOptionsForRoles, RoleOption } from '~pages/[organization]/settings/users/constants';

// The organization roles a Service Admin can assign from Admin Search — mirrors the API's
// SERVICE_ADMIN_EDITABLE_ROLES. Any other role the user holds (e.g. Mfa) is kept as it is. Owner is
// included, but the API rejects a second Owner in an organization.
const EDITABLE_ROLES = [
  Role.Owner,
  Role.Guest,
  Role.Installer,
  Role.Operator,
  Role.Developer,
  Role.Admin,
];

function toRoleOptions(roles: Role[]): RoleOption[] {
  return getRoleOptionsForRoles(roles).map((option) => ({ ...option, isDisabled: false }));
}

export function getEditableRoleOptions({ developerAccess }: { developerAccess?: boolean } = {}) {
  return toRoleOptions(
    EDITABLE_ROLES.filter((role) => role !== Role.Developer || developerAccess !== false),
  );
}

export function toEditableRoleOptions(roles: Role[]) {
  return toRoleOptions(roles.filter((role) => EDITABLE_ROLES.includes(role)));
}

export const roleOptionsSchema = z
  .array(z.object({ label: z.string(), value: z.nativeEnum(Role) }))
  .min(1, t('requiredField'));

export function getUserManagementErrorMap(): Record<string, string> {
  return {
    ORGANIZATION_USER_ALREADY_EXISTS: t('createUserErrDesc'),
    USER_ALREADY_EXISTS: t('createUserErrDesc1'),
    NOT_ALLOWED_TO_ASSIGN_ROLE: t('updateUserErrDesc'),
    ORGANIZATION_MUST_HAVE_ADMIN: t('adminSearchMustHaveAdmin'),
    ORGANIZATION_OWNER_ALREADY_EXISTS: t('adminSearchOwnerAlreadyExists'),
    USER_CANNOT_DELETE_SELF: t('adminSearchCannotDeleteSelf'),
    USER_SSO_EMAIL_CHANGE_NOT_ALLOWED: t('adminSearchSsoEmailChangeNotAllowed'),
    INVITATION_ALREADY_ACCEPTED: t('adminSearchInvitationAlreadyAccepted'),
  };
}

export function RolesField<TValues extends FieldValues>({
  control,
  name,
  error,
  options,
}: {
  control: Control<TValues>;
  name: Path<TValues>;
  error?: FieldError;
  options: RoleOption[];
}) {
  return (
    <FormControl isInvalid={Boolean(error)}>
      <FormLabel>{t('role')}</FormLabel>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select<RoleOption, true>
            components={components}
            options={options}
            isMulti={true}
            isClearable={false}
            menuPlacement="auto"
            name={field.name}
            ref={field.ref}
            value={field.value}
            onChange={field.onChange}
            onBlur={field.onBlur}
          />
        )}
      />
      <FormErrorMessage>{error?.message}</FormErrorMessage>
    </FormControl>
  );
}
